import { create } from 'zustand';
import events from '../config/events';
import { RoomEvent } from 'livekit-client';
import CONSTANTS from '../config/constants';

export const createEventStore = (initialState = {}) =>
  create((set, get) => ({
    stores: {},
    validEvents: events.validEvents,
    configuredEvents: {},
    generalEvents: {},
    participantEvents: {},
    roomEvents: {},
    registeredEvents: {},

    injectDependentStores: (stores) => {
      set({ stores });
    },

    storeState: (name) => {
      if (!get().stores[name])
        throw new Error(`${name} is not a valid dependency in this store.`);
      return get().stores[name].getState();
    },

    initialize: async (props) => {
      // setup some default listeners that will trigger events...
      window.addEventListener('offline', () => {
        get().trigger('onOffline');
      });

      window.addEventListener('online', () => {
        get().trigger('onOnline');
      });

      const roomEvents = {};
      const participantEvents = {};
      const generalEvents = {};

      Object.keys(props).forEach((event) => {
        // now if the prop matches an event, call register to add it...
        if (get().validEvents[event]) {
          const eventConfig = get().validEvents[event];
          if (eventConfig.context === 'room') {
            roomEvents[event] = {
              config: eventConfig,
              function: props[event],
            };
          } else if (eventConfig.context === 'participant') {
            participantEvents[event] = {
              config: eventConfig,
              function: props[event],
            };
          } else {
            generalEvents[event] = {
              config: eventConfig,
              function: props[event],
            };
          }
        }
      });

      set({ roomEvents, participantEvents, generalEvents });

      // Call the function to setup all the room events...
      get().setupGeneralEvents();
      get().setupRoomEvents();

      const room = get().storeState('appStore').room;

      if (room) {
        // provision all events for any existing participants once the room is connected...
        room.on(RoomEvent.Connected, () => {
          try {
            get().setupParticipantEvents(room.localParticipant);
          } catch (e) {
            console.log(e);
          }

          // now loop all the remote participants and setup the events for them...
          room.remoteParticipants.forEach((participant) => {
            try {
              get().setupParticipantEvents(participant);
            } catch (e) {
              console.log(e);
            }
          });
        });

        // now whenever a new participant connects, lets register all participant events...
        room.on(RoomEvent.ParticipantConnected, (participant) => {
          get().setupParticipantEvents(participant);
        });
      }
    },

    setupGeneralEvents: () => {
      Object.keys(get().generalEvents).forEach((eventName) => {
        const event = get().generalEvents[eventName];
        get().register(eventName, event.function);
      });
    },

    setupRoomEvents: () => {
      const room = get().storeState('appStore').room;
      Object.keys(get().roomEvents).forEach((eventName) => {
        const event = get().roomEvents[eventName];
        // add the listener to the room...
        //console.log(`Registered Event: ${eventName} attached to room.`);
        if (room) {
          room.on(event.config.liveKitEvent, (...args) => {
            get().executeEvent(event, args);
          });
        }
      });

      if (room) {
        room.on('dataReceived', (payload, participant, kind) => {
          const dataMessage = get()
            .storeState('appStore')
            .mediaClient.processDataMessage(payload, participant, kind);

          if (dataMessage.event === CONSTANTS.EVENT_TYPES.ANNOTATIONS.ADDED) {
            // call to add this to the system...
            get()
              .storeState('examSessionStore')
              .addAnnotation(dataMessage.data.payload);
            // now trigger that a new annotation was added as well..
            get().trigger('onAnnotationAdded', { dataMessage });
            console.log(dataMessage);

            // Now if the annocation was the recording file is ready, we need to load the metadata...
            if (
              dataMessage.data.payload.typeId === 'recording-file-available'
            ) {
              get().storeState('examSessionStore').updateRecordingMetadata();
            }
          } else if (
            dataMessage.event === CONSTANTS.EVENT_TYPES.ANNOTATIONS.DELETED
          ) {
            get()
              .storeState('examSessionStore')
              .removeAnnotation(dataMessage.data.payload.sid);
          } else if (
            dataMessage.event === CONSTANTS.EVENT_TYPES.RECORDING.TRACK_CHANGED
          ) {
            console.log(dataMessage);
          }
        });
      }
    },

    setupParticipantEvents: (participant) => {
      Object.keys(get().participantEvents).forEach((eventName) => {
        const event = get().participantEvents[eventName];
        // add the listener to the participant...
        //console.log(`Registered Event: ${eventName} attached to participant (${participant.participantInfo.identity}).`, event);
        participant.on(event.config.liveKitEvent, (...args) => {
          get().executeEvent(event, args);
        });
      });
    },

    executeEvent: (event, args) => {
      try {
        const functionToRun = event.function;
        if (event.config.arguments) {
          const argMap = {};
          event.config.arguments.forEach((key, index) => {
            argMap[key] = args[index];
          });
          //Only run if the filter function returns true or there is no filter...
          if (
            event.config.filter &&
            typeof event.config.filter === 'function' &&
            event.config.filter(argMap)
          ) {
            functionToRun(argMap);
          }
        } else {
          // if there are no defined arguments, just call the function...
          functionToRun();
        }
      } catch (e) {
        console.log(e);
      }
    },

    /**
     * Registers a callback function for a given event.
     *
     * Validates that the provided function is indeed a function and the event
     * is a valid event as per the validEvents list. If valid, the function
     * is registered to be called when the event is triggered.
     *
     * @param {string} event - The event name to register the callback for.
     * @param {Function} fn - The callback function to execute when the event is triggered.
     * @throws Will throw an error if the function is not a valid function or if the event is not found.
     */
    register: (event, fn) => {
      // ensure the function being passed in is a function
      if (typeof fn !== 'function') {
        throw Error('Function passed to register must be a function.');
      }
      // ensure the event is a valid event.
      if (!Object.keys(get().validEvents).includes(event)) {
        throw Error('Event not found.');
      }

      // register the event
      get().registeredEvents[event] = fn;
    },

    /**
     * Removes a registered event.
     *
     * This function deletes the event from the registeredEvents object,
     * effectively unregistering it so it will no longer be triggered.
     *
     * @param {string} event - The event name to remove from the registered events.
     */

    remove: async (event) => {
      // remove the event from registeredEvents
      delete get().registeredEvents[event];
    },

    /**
     * Trigger a registered event.
     *
     * This function triggers the provided event, passing the options object
     * to the registered callback function. If the event is not found, it
     * will log a message to the console.
     *
     * @param {string} event - The event name to trigger.
     * @param {Object} [options={}] - The options object to pass to the registered callback function.
     */
    trigger: async (event, options = {}) => {
      // if the event is registered, call it
      if (get().registeredEvents[event]) {
        get().registeredEvents[event](options);
      }
    },
  }));
